import { LockPasswordIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, useForm } from "@inertiajs/react";
import type { FormEvent } from "react";
import Dashboard from "@/components/dashboard";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	errors?: Record<string, string[]>;
}

export default function Edit({ user, errors }: Props) {
	const { data, setData, patch, processing } = useForm({
		password_challenge: "",
		password: "",
		password_confirmation: "",
	});

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		patch("/password");
	};

	return (
		<>
			<Head title="Změna hesla" />
			<Dashboard user={user}>
				<div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
					<div className="flex items-center gap-3">
						<div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
							<HugeiconsIcon
								icon={LockPasswordIcon}
								strokeWidth={2}
								className="size-5 text-primary"
							/>
						</div>
						<div>
							<h2 className="font-semibold text-2xl">Změna hesla</h2>
							<p className="text-muted-foreground text-sm">
								Aktualizujte své přihlašovací heslo
							</p>
						</div>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>Nové heslo</CardTitle>
							<CardDescription>
								Pro změnu hesla zadejte své současné heslo a poté nové heslo.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<form onSubmit={handleSubmit}>
								<FieldGroup>
									<Field>
										<FieldLabel htmlFor="password_challenge">
											Současné heslo
										</FieldLabel>
										<Input
											id="password_challenge"
											name="password_challenge"
											type="password"
											value={data.password_challenge}
											onChange={(e) =>
												setData("password_challenge", e.target.value)
											}
											aria-invalid={!!errors?.password_challenge}
											required
											autoComplete="current-password"
										/>
										{errors?.password_challenge && (
											<FieldDescription className="text-destructive">
												{errors.password_challenge.join(", ")}
											</FieldDescription>
										)}
									</Field>

									<Field>
										<FieldLabel htmlFor="password">Nové heslo</FieldLabel>
										<Input
											id="password"
											name="password"
											type="password"
											value={data.password}
											onChange={(e) => setData("password", e.target.value)}
											aria-invalid={!!errors?.password}
											required
											autoComplete="new-password"
										/>
										{errors?.password && (
											<FieldDescription className="text-destructive">
												{errors.password.join(", ")}
											</FieldDescription>
										)}
									</Field>

									<Field>
										<FieldLabel htmlFor="password_confirmation">
											Potvrzení nového hesla
										</FieldLabel>
										<Input
											id="password_confirmation"
											name="password_confirmation"
											type="password"
											value={data.password_confirmation}
											onChange={(e) =>
												setData("password_confirmation", e.target.value)
											}
											aria-invalid={!!errors?.password_confirmation}
											required
											autoComplete="new-password"
										/>
										{errors?.password_confirmation && (
											<FieldDescription className="text-destructive">
												{errors.password_confirmation.join(", ")}
											</FieldDescription>
										)}
									</Field>

									<Field>
										<Button type="submit" disabled={processing}>
											Změnit heslo
										</Button>
									</Field>
								</FieldGroup>
							</form>
						</CardContent>
					</Card>
				</div>
			</Dashboard>
		</>
	);
}
